import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkout } from '@pdqdev/pdq-shared/dist/interfaces';
import { Shippings } from '@app/pages/checkout-app/types';
import Input from '@app/react-common/components/input/Input';
import { CurrencyInput } from '@app/react-common/components/currency-input/CurrencyInput';
import { useClickedOutside } from '@app/react-common/hooks/useClickOutside';
import { groupMethodsByCategory, getCategoryFallback, CategoryGroup } from './categoryGrouping';

interface GroupedDeliveryMethodsProps {
  methods: Shippings[];
  config: Checkout.CategoryBasedShippingConfig;
  shopCurrency: string;
  onCategoryFallbackChange: (fallback: Checkout.CategoryShippingFallback) => void;
  renderMethodRow: (method: Shippings) => React.ReactNode;
}

const CategoryFallbackEditor = ({
  fallback,
  shopCurrency,
  onChange,
}: {
  fallback: Checkout.CategoryShippingFallback;
  shopCurrency: string;
  onChange: (fallback: Checkout.CategoryShippingFallback) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Checkout.CategoryShippingFallback>(fallback);
  const draftRef = useRef(fallback);
  const editorRef = useRef<HTMLDivElement>(null);

  const updateDraft = (next: Checkout.CategoryShippingFallback) => {
    draftRef.current = next;
    setDraft(next);
  };

  const startEditing = () => {
    updateDraft(fallback);
    setEditing(true);
  };

  // Persist name + rate together only when clicking outside the editor while editing.
  const handleClickedOutside = useCallback(() => {
    if (!editing) {
      return;
    }
    onChange(draftRef.current);
    setEditing(false);
  }, [editing, onChange]);

  useClickedOutside(editorRef, handleClickedOutside);

  useEffect(() => {
    if (editing) {
      editorRef.current?.querySelector('input')?.focus();
    }
  }, [editing]);

  return (
    <div ref={editorRef} className="delivery-methods-list__group-by__fallback" onClick={(e) => e.stopPropagation()}>
      <span className="delivery-methods-list__group-by__fallback__label">Fallback method:</span>
      {editing ? (
        <div className="delivery-methods-list__group-by__fallback__fields">
          <Input
            type="text"
            value={draft.name}
            onChange={(e) => updateDraft({ ...draft, name: e.target.value })}
            placeholder="Fallback name"
          />
          <CurrencyInput
            name={`fallback-rate-${fallback.categoryValue ?? 'no-value'}`}
            value={draft.rate}
            setValue={(rate) => updateDraft({ ...draft, rate })}
            setValid={() => undefined}
            currencySign={shopCurrency}
            minValue={0}
          />
        </div>
      ) : (
        <button type="button" className="delivery-methods-list__group-by__fallback__pill" onClick={startEditing}>
          <span>
            {fallback.name || 'Add name'} | {shopCurrency}
            {fallback.rate ?? 0}
          </span>
          <i className="icon icon-oeag" />
        </button>
      )}
    </div>
  );
};

const CategoryCard = ({
  group,
  fallback,
  shopCurrency,
  onCategoryFallbackChange,
  renderMethodRow,
}: {
  group: CategoryGroup;
  fallback: Checkout.CategoryShippingFallback;
  shopCurrency: string;
  onCategoryFallbackChange: (fallback: Checkout.CategoryShippingFallback) => void;
  renderMethodRow: (method: Shippings) => React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="delivery-methods-list__group-by__card">
      <div className="delivery-methods-list__group-by__card__header" onClick={() => setIsOpen((prev) => !prev)}>
        <span className="delivery-methods-list__group-by__card__label">{group.label}</span>
        <div className="delivery-methods-list__group-by__card__header-right">
          <CategoryFallbackEditor fallback={fallback} shopCurrency={shopCurrency} onChange={onCategoryFallbackChange} />
          <i className={`icon icon-arrow_down ${isOpen ? '' : 'collapsed'}`} />
        </div>
      </div>
      {isOpen && (
        <div className="delivery-methods-list__group-by__card__body">
          {group.methods.map((method) => (
            <React.Fragment key={method.id}>{renderMethodRow(method)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export const GroupedDeliveryMethods = ({
  methods,
  config,
  shopCurrency,
  onCategoryFallbackChange,
  renderMethodRow,
}: GroupedDeliveryMethodsProps) => {
  const groups = groupMethodsByCategory(methods, config.groupBy);

  return (
    <div className="delivery-methods-list__group-by__grouped">
      {groups.map((group) => (
        <CategoryCard
          key={group.label}
          group={group}
          fallback={getCategoryFallback(config, group.category)}
          shopCurrency={shopCurrency}
          onCategoryFallbackChange={onCategoryFallbackChange}
          renderMethodRow={renderMethodRow}
        />
      ))}
    </div>
  );
};
